import type { AnyRouteMatch } from '@tanstack/react-router';
import { useRouterState } from '@tanstack/react-router';

// Corresponds to route files in /ui/src/routes/$org/$agent/
const VALID_FEATURES = [
  'equipment',
  'alarms',
  'trends',
  'reports',
  'facility-performance',
] as const;

export type ValidFeature = (typeof VALID_FEATURES)[number];

const FEATURE_ROUTE_IDS: Record<ValidFeature, `/$org/$agent/${ValidFeature}`> = {
  equipment: '/$org/$agent/equipment',
  alarms: '/$org/$agent/alarms',
  trends: '/$org/$agent/trends',
  reports: '/$org/$agent/reports',
  'facility-performance': '/$org/$agent/facility-performance',
};

const ROUTE_ID_TO_FEATURE = Object.entries(FEATURE_ROUTE_IDS).reduce(
  (acc, [feature, routeId]) => {
    acc[routeId] = feature as ValidFeature;
    return acc;
  },
  {} as Record<string, ValidFeature>
);

const FEATURE_FALLBACK_ORDER: readonly ValidFeature[] = ['alarms', 'equipment'] as const;
export const DEFAULT_FEATURE: ValidFeature = FEATURE_FALLBACK_ORDER[0];

const findMatchedFeature = (
  matches: ReadonlyArray<Pick<AnyRouteMatch, 'routeId'>>
): ValidFeature | undefined => {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    const feature = ROUTE_ID_TO_FEATURE[match.routeId];
    if (feature) {
      return feature;
    }
  }

  return undefined;
};

/**
 * Returns the currently matched feature route (/$org/$agent/{feature}).
 * Falls back to product-preferred order (alarms -> equipment) when not matched.
 */
export const getCurrentFeature = (
  matches: ReadonlyArray<Pick<AnyRouteMatch, 'routeId'>>
): ValidFeature => {
  return findMatchedFeature(matches) ?? DEFAULT_FEATURE;
};

export const useCurrentFeature = (): ValidFeature =>
  useRouterState({
    select: (state) => getCurrentFeature(state.matches ?? []),
  });
